import { AUTOFRAME_MAX_VIDEO_DURATION_SECONDS } from 'components/VideoTemplateEditor/constants';
import { FramingMethod } from 'components/VideoTemplateEditor/types';
import { IndexedStep, Step } from 'components/Wizard';
import { AspectRatioName } from 'types';
import bem from 'utils/bem';
import { millisToSec, secToMillis } from 'utils/time';
import { VideoWizardObjective } from '../types';
import {
  VideoFlowStep,
  VideoFlowSubmitConfig,
  VideoFlowUploadArgs,
} from './types';

export const block = bem('video-flow');

export function createUploadArgs({
  aspectRatioName,
  clipEndMillis,
  clipStartMillis,
  exportState,
  isAutoFrame,
  isFullEpisode,
  language,
  objective,
  platform,
  transcribe,
  suggestedClipDurationMillis,
  useYoutubeMostReplayed,
  videoDurationMillis,
  videoFile,
  videoSrc,
  videoType,
}: VideoFlowSubmitConfig): VideoFlowUploadArgs {
  const baseArgs = {
    aspectRatioName,
    durationMillis: videoDurationMillis,
    exportState,
    isAutoFrame,
    language,
    platform,
    src: videoFile || videoSrc,
    transcribe,
    trimEndMillis: clipEndMillis,
    trimStartMillis: clipStartMillis,
    videoType,
  };

  const singleClipArgs = {
    initiateExport: isFullEpisode,
    isClipSuggestion: false,
    isFullEpisodeExport: isFullEpisode,
    videoPodcastType: isFullEpisode ? 'FullEpisode' : 'SingleClip',
    waitForUpload: true,
  } as const;

  const multiClipArgs = {
    clipSuggestionLengthSecs: millisToSec(suggestedClipDurationMillis),
    isClipSuggestion: true,
    useYoutubeMostReplayed,
    videoPodcastType: 'MultipleClips',
    waitForUpload: objective === 'YouTubeMostReplayed',
  } as const;

  return {
    ...baseArgs,
    ...(objective === 'SingleClip' ? singleClipArgs : multiClipArgs),
  };
}

export function isVideoFlowStep(stepId: string): stepId is VideoFlowStep {
  return Object.values(VideoFlowStep).includes(stepId as VideoFlowStep);
}

export function isNavigatingToEarlierStep(
  targetStepId: VideoFlowStep,
  toStep: IndexedStep,
  steps: Step<VideoFlowStep>[],
) {
  const targetStepIndex = steps.findIndex(step => step.stepId === targetStepId);
  return targetStepIndex >= 0 && targetStepIndex >= toStep.index;
}

export function canBacktrackFromStep(stepId: VideoFlowStep) {
  return ![VideoFlowStep.PROGRESS, VideoFlowStep.EXPORT].includes(stepId);
}

export interface GetFramingMethodArgs {
  autoFramePreference?: boolean | null;
  objective: VideoWizardObjective | undefined;
  aspectRatioName: AspectRatioName;
  outputDurationMillis?: number;
}

export function getFramingMethod({
  autoFramePreference,
  objective,
  aspectRatioName,
  outputDurationMillis,
}: GetFramingMethodArgs): FramingMethod {
  if (autoFramePreference === true) {
    return 'autoframe';
  }

  if (autoFramePreference === false) {
    return 'fixed';
  }

  if (
    objective === 'SingleClip' ||
    aspectRatioName === 'landscape' ||
    outputDurationMillis > secToMillis(AUTOFRAME_MAX_VIDEO_DURATION_SECONDS)
  ) {
    return 'fixed';
  }

  return 'autoframe';
}
